<template>
    <div v-if="!!planningItem">
        <div v-if="attachments.length" class="attachment-grid">
            <div
                :key="attachment.planning_item_attachment_id"
                v-for="attachment in attachments"
                class="attachment-tile"
            >
                <auth-image
                    v-if="isImage(attachment)"
                    class="attachment-thumb"
                    :file-name="attachment.storage_url"
                    :alt="attachment.storage_url"
                    @click="openPreview(attachment)"
                />
                <div v-else class="attachment-thumb attachment-file" @click="download(attachment)">
                    <md-icon>insert_drive_file</md-icon>
                    <span class="attachment-file-name">{{ attachment.storage_url }}</span>
                </div>
                <div class="attachment-actions">
                    <md-button class="md-icon-button md-dense" @click="download(attachment)" title="Download">
                        <md-icon>vertical_align_bottom</md-icon>
                    </md-button>
                    <md-button
                        class="md-icon-button md-dense md-accent"
                        @click="confirmDelete(attachment)"
                        title="Verwijderen"
                    >
                        <md-icon>delete</md-icon>
                    </md-button>
                </div>
            </div>
        </div>
        <div v-else style="margin-top: 10px">Geen bijlagen</div>

        <md-dialog :md-active.sync="showPreview" class="preview-dialog">
            <md-dialog-content v-if="previewAttachment">
                <auth-image
                    class="preview-image"
                    :file-name="previewAttachment.storage_url"
                    :alt="previewAttachment.storage_url"
                />
            </md-dialog-content>
            <md-dialog-actions>
                <md-button class="md-primary" @click="download(previewAttachment)">Download</md-button>
                <md-button class="md-primary" @click="showPreview = false">Sluiten</md-button>
            </md-dialog-actions>
        </md-dialog>

        <md-dialog-confirm
            :md-active.sync="showDeleteConfirm"
            md-title="Bijlage verwijderen?"
            md-content="Deze bijlage wordt definitief verwijderd."
            md-confirm-text="Verwijderen"
            md-cancel-text="Annuleren"
            @md-confirm="remove()"
        />
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import * as toastr from 'toastr';
import { saveAs } from 'file-saver';
import { IPlanningItemAttachment, IPlanningItemEntity } from '@phrospr/api-backend';
import AuthImage from '@/components/AuthImage.vue';
import {
    deletePlanningItemAttachment,
    downloadPlanningItemAttachment,
} from '@/resources/phrospr-api/planning-item.service';

const IMAGE_EXTENSION_REGEX = /\.(png|jpe?g|gif|webp)$/i;

@Component({
    components: { AuthImage },
})
export default class PlanningDetailAttachmentsView extends Vue {
    showPreview = false;
    previewAttachment: IPlanningItemAttachment = null;
    showDeleteConfirm = false;
    attachmentToDelete: IPlanningItemAttachment = null;

    get planningItem(): IPlanningItemEntity {
        return this.$store.state.planningItem;
    }

    get attachments(): IPlanningItemAttachment[] {
        return (this.planningItem.attachments ?? []).filter(attachment => !!attachment.storage_url);
    }

    public isImage(attachment: IPlanningItemAttachment): boolean {
        return IMAGE_EXTENSION_REGEX.test(attachment.storage_url);
    }

    public openPreview(attachment: IPlanningItemAttachment) {
        this.previewAttachment = attachment;
        this.showPreview = true;
    }

    public async download(attachment: IPlanningItemAttachment) {
        try {
            const blob = await downloadPlanningItemAttachment(attachment.storage_url);
            saveAs(blob, attachment.storage_url);
        } catch (err) {
            console.error(err);
            toastr.error('Downloaden van de bijlage is mislukt');
        }
    }

    public confirmDelete(attachment: IPlanningItemAttachment) {
        this.attachmentToDelete = attachment;
        this.showDeleteConfirm = true;
    }

    public async remove() {
        if (!this.attachmentToDelete) return;
        try {
            await deletePlanningItemAttachment(this.attachmentToDelete.planning_item_attachment_id);
            toastr.success('Bijlage verwijderd');
            await this.$store.dispatch('refreshPlanningItem');
        } catch (err) {
            console.error(err);
            toastr.error('Verwijderen van de bijlage is mislukt');
        } finally {
            this.attachmentToDelete = null;
        }
    }
}
</script>

<style scoped>
.attachment-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 8px;
    margin-top: 10px;
}

.attachment-tile {
    display: flex;
    flex-direction: column;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
}

.attachment-thumb {
    height: 100px;
    display: block;
}

.attachment-file {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: #f0f0f0;
    padding: 4px;
}

.attachment-file-name {
    font-size: 10px;
    word-break: break-all;
    text-align: center;
    max-height: 30px;
    overflow: hidden;
}

.attachment-actions {
    display: flex;
    justify-content: space-around;
}

.preview-dialog {
    max-width: 95vw;
}

.preview-image {
    max-width: 90vw;
    max-height: 70vh;
}
</style>
